import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Truck, Utensils, Search, CalendarDays } from "lucide-react";

const ROLES = [
  { key: "customer", label: "Foodie", desc: "Find & follow trucks", icon: <Search className="w-5 h-5" /> },
  { key: "owner", label: "Truck Owner", desc: "List & get booked", icon: <Utensils className="w-5 h-5" /> },
  { key: "organizer", label: "Event Host", desc: "Post events for vendors", icon: <CalendarDays className="w-5 h-5" /> },
];

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "customer" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      toast.success(`Welcome to FlavorFlow, ${u.name}!`);
      navigate(u.role === "owner" ? "/dashboard/owner" : u.role === "organizer" ? "/dashboard/organizer" : "/explore");
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-5 py-16">
      <div className="flex items-center gap-2 justify-center mb-6">
        <span className="w-10 h-10 rounded-full bg-[#0FB5A6] flex items-center justify-center"><Truck className="w-5 h-5 text-white" /></span>
        <span className="font-display font-extrabold text-2xl">FlavorFlow</span>
      </div>
      <div className="bg-white rounded-2xl border border-teal-100 p-8 gr-shadow">
        <h1 className="font-display text-3xl font-black">Create your account</h1>
        <p className="text-slate-500 mt-1">Free to join. Pick what fits you.</p>

        <div className="grid grid-cols-3 gap-2 mt-6">
          {ROLES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => set("role", r.key)}
              data-testid={`role-${r.key}`}
              className={`p-3 rounded-xl border text-center transition-colors ${form.role === r.key ? "border-[#0FB5A6] bg-teal-50" : "border-teal-100 hover:bg-teal-50/50"}`}
            >
              <span className={`w-9 h-9 rounded-full mx-auto flex items-center justify-center ${form.role === r.key ? "bg-[#0FB5A6] text-white" : "bg-slate-100 text-slate-500"}`}>{r.icon}</span>
              <p className="text-xs font-bold mt-2">{r.label}</p>
              <p className="text-[10px] text-slate-400 leading-tight">{r.desc}</p>
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4 mt-6" data-testid="register-form">
          <div className="space-y-1.5"><Label>Name</Label><Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="register-name" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="register-email" /></div>
          <div className="space-y-1.5"><Label>Password</Label><Input type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="register-password" /></div>
          <Button type="submit" disabled={loading} className="w-full rounded-full h-12 font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white active:scale-95 transition-transform" data-testid="register-submit">
            {loading ? "Creating…" : "Create account"}
          </Button>
        </form>
        <p className="text-sm text-slate-500 mt-5 text-center">Already have an account? <Link to="/login" className="text-[#0FB5A6] font-bold">Log in</Link></p>
      </div>
    </div>
  );
}
